import React from "react";
import { SearchTextField } from "@packsize/components";
import { Authorized } from "@packsize/authentication";
import {
	Card,
	CircularProgress,
	Grid,
	IconButton,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import { type Role, type User } from "@packsize/identity-api-sdk";
import { FormattedMessage, useIntl } from "react-intl";
import Refresh from "@mui/icons-material/Refresh";

import { ConfigurationPageLayout } from "@/views/configuration/components";
import { search } from "@/utils/search";

import { getUserRoles } from "./users.utils";
import { ManageUsersButton } from "./manage-users-button";

interface UsersTemplateProps {
	readonly users: User[];
	readonly roles: Role[];
	readonly isLoading?: boolean;
	readonly isError?: boolean;
	readonly onRetry?: () => void;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export const UsersTemplate: React.FC<UsersTemplateProps> = ({ users, roles, isLoading, isError, onRetry }) => {
	const intl = useIntl();

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(50);
	const [searchTerm, setSearchTerm] = React.useState("");
	const filteredUsers =
		searchTerm.length < 3 ? users : search(searchTerm, users, ["userName", "firstName", "lastName"]);

	const actions = (
		<>
			<SearchTextField
				value={searchTerm}
				onChange={(value) => {
					setSearchTerm(value);
					setPage(0);
				}}
				placeholder={intl.formatMessage({ id: "Search users...", defaultMessage: "Search users..." })}
				size="small"
				data-testid="users__search__users"
				sx={{ backgroundColor: "common.white", width: "300px" }}
			/>
			<Authorized permissions={["TenantAdmin", "ManageCompanyAccess", "AccountManager"]}>
				<ManageUsersButton />
			</Authorized>
		</>
	);

	return (
		<ConfigurationPageLayout title={intl.formatMessage({ id: "Users", defaultMessage: "Users" })} actions={actions}>
			<Card component={Grid} container direction="column">
				<TableContainer sx={{ flex: 1 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>
									<FormattedMessage id="Username" defaultMessage="Username" />
								</TableCell>

								<TableCell>
									<FormattedMessage id="First Name" defaultMessage="First Name" />
								</TableCell>

								<TableCell>
									<FormattedMessage id="Last Name" defaultMessage="Last Name" />
								</TableCell>

								<TableCell>
									<FormattedMessage id="Roles" defaultMessage="Roles" />
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading === true && (
								<StyledTableRow sx={{ height: 100 }}>
									<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
										<CircularProgress color="info" data-testid="users__progress__loading" />
									</TableCell>
								</StyledTableRow>
							)}
							{isError === true && isLoading === false && (
								<StyledTableRow sx={{ height: 100 }}>
									<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
										<Grid container justifyContent="center" alignItems="center" gap={2}>
											<Typography component="span" color="error" data-testid="users__text__error">
												<FormattedMessage
													id="Failed to load users. Please try again."
													defaultMessage="Failed to load users. Please try again."
												/>
											</Typography>
											<IconButton onClick={onRetry} data-testid="users__button__retry">
												<Refresh />
											</IconButton>
										</Grid>
									</TableCell>
								</StyledTableRow>
							)}
							{!isLoading && !isError && filteredUsers.length === 0 && (
								<StyledTableRow>
									<TableCell colSpan={1000} align="center">
										<Typography color="textDisabled" data-testid="users__text__no-users">
											<FormattedMessage id="No Users to Display" defaultMessage="No Users to Display" />
										</Typography>
									</TableCell>
								</StyledTableRow>
							)}
							{!isLoading &&
								!isError &&
								filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
									<StyledTableRow key={user.id}>
										<TableCell sx={{ fontWeight: 500 }}>{user.userName}</TableCell>
										<TableCell>{user.firstName}</TableCell>
										<TableCell>{user.lastName}</TableCell>
										<TableCell>
											{getUserRoles(user, roles)
												.map((r) => r.name)
												.join(", ")}
										</TableCell>
									</StyledTableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[50, 100, 500]}
					component="div"
					count={filteredUsers.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={(_, pageNumber) => setPage(pageNumber)}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value));
						setPage(0);
					}}
				/>
			</Card>
		</ConfigurationPageLayout>
	);
};
